import React from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useWebSocketContext } from '../../../services/websocket/context/WebSocketContext';
import { WebSocketMessage } from '../../../services/websocket/hooks/useWebSocket';
import PlaceDetailsPanel from '../../components/maps/PlaceDetailsPanel';
import ActionRequestCard from '../../components/chat/ActionRequestCard';
import TripDraftCard, { TripDraftShape } from '../../components/chat/TripDraftCard';
import ChatAttachment from '../../components/chat/ChatAttachment';
import { ActionRequest, ActionResolution, collectFallbackActivities } from '../../../services/trips/actionRequests';
import { getTrips } from '../../../services/trips/api/tripsApi';
import { extractTripUuid } from '../../../services/trips/utils/idUtils';
import { PlaceDetails } from '../../components/maps/placeDetails';
import {
  LocationRecommendation,
  fetchPlaceDetailsFromGoogle,
  fetchPlaceGeometryFromGoogle,
  getPlaceLocation,
  normalizeLocationRecommendations,
  normalizePlaceId,
} from '../../components/maps/googlePlacesService';
import {
  ConversationSummary,
  formatConversationTimestamp,
  getConversation,
  listConversations,
  truncateConversationPreview,
} from '../../../services/conversations/conversationsApi';
import FormattedChatMessage from '../../components/chat/FormattedChatMessage';
import '../../components/maps/MapPlaceSidebar.css';
import './AskAiPage.css';

interface AgentResponse {
  intent: string;
  confidence: number;
  message: string;
  locations_recommendations?: Array<Record<string, unknown>>;
  activities?: Array<Record<string, any>>;
  trip_draft?: TripDraftShape;
  action_request?: ActionRequest;
}

interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
  isError?: boolean;
  intent?: string;
  locations_recommendations?: LocationRecommendation[];
  // Top-level activities array from the agent response ({name, description}) —
  // fallback stops for draft-card creates (collectFallbackActivities).
  activities?: Array<Record<string, any>>;
  trip_draft?: TripDraftShape;
  action_request?: ActionRequest;
  actionResolution?: ActionResolution;
  createdTripId?: string;
  actionResolutionNote?: string;
}

const promptChips = [
  { label: 'Weekend in the mountains', icon: '🏔️' },
  { label: 'Best food cities in Asia', icon: '🍜' },
  { label: 'Family-friendly beaches', icon: '🏖️' },
  { label: 'Cheap European getaways', icon: '🗺️' },
];

const welcomeMessage =
  "Hi! I'm your EnRoute AI — ask me anything about destinations, activities, food, or logistics. I'll drop suggestions right on the map.";

const parseAgentResponse = (text: string): { response: AgentResponse | null; isError: boolean } => {
  if (!text || typeof text !== 'string') {
    return { response: null, isError: false };
  }

  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return { response: null, isError: false };
  }

  try {
    const parsed = JSON.parse(trimmed) as any;
    const response = parsed.response?.intent ? parsed.response : parsed.intent ? parsed : null;

    if (response?.intent && response.message !== undefined && response.confidence !== undefined) {
      return { response, isError: false };
    }
  } catch {
    return { response: null, isError: false };
  }

  return { response: null, isError: false };
};

const formatConversationHistory = (messages: Array<{
  message_id?: number | string;
  created_at?: number;
  text?: string;
  actor?: string;
}>): ChatMessage[] =>
  messages.map((message) => {
    const parsed = message.actor === 'assistant'
      ? parseAgentResponse(message.text || '')
      : { response: null, isError: false };

    return {
      id: message.message_id?.toString() || `history-${Date.now()}-${Math.random()}`,
      message: parsed.response?.message || message.text || '',
      isUser: message.actor === 'user',
      timestamp: new Date((message.created_at || Date.now() / 1000) * 1000),
      isLoading: false,
      isError: false,
      intent: parsed.response?.intent,
      locations_recommendations: normalizeLocationRecommendations(parsed.response?.locations_recommendations),
      activities: parsed.response?.activities,
      trip_draft: parsed.response?.trip_draft,
      action_request: parsed.response?.action_request,
    };
  });

const AskAiPage: React.FC = () => {
  const webSocket = useWebSocketContext();
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const recommendationMarkersRef = React.useRef<any[]>([]);
  const chatMessagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const responseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingPlaceSelectionRef = React.useRef<{ placeId: string; queryFallback?: string } | null>(null);
  const placeRequestIdRef = React.useRef(0);
  const sessionMenuRef = React.useRef<HTMLDivElement | null>(null);

  const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([]);
  const [currentInput, setCurrentInput] = React.useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = React.useState(false);
  const [isLoadingConversation, setIsLoadingConversation] = React.useState(false);
  const [conversationId, setConversationId] = React.useState('');
  // UUID of the trip already created for the current chat, if any — used to
  // block a second CREATE_TRIP proposal for the same conversation.
  const [conversationTripId, setConversationTripId] = React.useState<string | undefined>(undefined);
  const [conversations, setConversations] = React.useState<ConversationSummary[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = React.useState(false);
  const [conversationsError, setConversationsError] = React.useState<string | null>(null);
  const [isSessionMenuOpen, setIsSessionMenuOpen] = React.useState(false);
  const [mapType, setMapType] = React.useState<'roadmap' | 'satellite'>('roadmap');
  const [mapReady, setMapReady] = React.useState(false);
  const [suggestedPlacesCount, setSuggestedPlacesCount] = React.useState(0);
  const [selectedPlace, setSelectedPlace] = React.useState<PlaceDetails | null>(null);
  const [isLoadingPlace, setIsLoadingPlace] = React.useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = React.useState<string | null>(null);
  const [selectedRecommendation, setSelectedRecommendation] = React.useState<LocationRecommendation | null>(null);
  const [userLocation, setUserLocation] = React.useState<{ lat: number; lng: number } | null>(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasApiKey = Boolean(googleMapsApiKey);

  const sessionLabel = React.useMemo(() => {
    if (!conversationId) return 'New chat';

    const currentConversation = conversations.find((item) => item.conversation_id === conversationId);
    if (currentConversation) {
      return truncateConversationPreview(currentConversation.preview);
    }

    const firstUserMessage = chatMessages.find((message) => message.isUser && message.message.trim());
    if (firstUserMessage) {
      return truncateConversationPreview(firstUserMessage.message);
    }

    return 'Chat';
  }, [chatMessages, conversationId, conversations]);

  const loadConversationsList = React.useCallback(async () => {
    setIsLoadingConversations(true);
    setConversationsError(null);

    try {
      const nextConversations = await listConversations();
      setConversations(nextConversations);
    } catch (err: any) {
      setConversationsError(err.message || 'Could not load previous chats.');
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  const finishAssistantMessage = React.useCallback((
    message: string,
    isError = false,
    agentResponse?: AgentResponse
  ) => {
    setChatMessages((current) => {
      const updated = [...current];
      const lastMessageIndex = updated.length - 1;
      const messageData: Partial<ChatMessage> = {
        message,
        isLoading: false,
        isError,
        intent: agentResponse?.intent,
        locations_recommendations: normalizeLocationRecommendations(agentResponse?.locations_recommendations),
        activities: agentResponse?.activities,
        trip_draft: agentResponse?.trip_draft,
        action_request: agentResponse?.action_request,
      };

      if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
        updated[lastMessageIndex] = {
          ...updated[lastMessageIndex],
          ...messageData,
        };
        return updated;
      }

      return [
        ...updated,
        {
          id: `assistant-${Date.now()}`,
          message,
          isUser: false,
          timestamp: new Date(),
          isLoading: false,
          isError,
          intent: agentResponse?.intent,
          locations_recommendations: normalizeLocationRecommendations(agentResponse?.locations_recommendations),
          activities: agentResponse?.activities,
          trip_draft: agentResponse?.trip_draft,
          action_request: agentResponse?.action_request,
        },
      ];
    });

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    setIsWaitingForResponse(false);
  }, []);

  const clearRecommendationMarkers = React.useCallback(() => {
    recommendationMarkersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    recommendationMarkersRef.current = [];
  }, []);

  const resetChatSession = React.useCallback(() => {
    setChatMessages([]);
    setConversationId('');
    setConversationTripId(undefined);
    setSelectedPlace(null);
    setSelectedPlaceId(null);
    setSelectedRecommendation(null);
    setSuggestedPlacesCount(0);
    clearRecommendationMarkers();
  }, [clearRecommendationMarkers]);

  const fetchPlaceDetails = React.useCallback(async (placeId: string, queryFallback?: string) => {
    const normalizedId = normalizePlaceId(placeId);
    const requestId = placeRequestIdRef.current + 1;
    placeRequestIdRef.current = requestId;

    setIsLoadingPlace(true);
    setSelectedPlaceId(normalizedId || placeId);

    const mapElement = mapInstanceRef.current;
    if (!mapElement) {
      pendingPlaceSelectionRef.current = { placeId: normalizedId || placeId, queryFallback };
      setIsLoadingPlace(false);
      return;
    }

    const details = await fetchPlaceDetailsFromGoogle(mapElement, normalizedId || placeId, {
      queryFallback,
      locationBias: userLocation || undefined,
    });

    if (requestId !== placeRequestIdRef.current) return;

    setIsLoadingPlace(false);
    setSelectedPlace(details);

    if (details && mapInstanceRef.current) {
      const location = getPlaceLocation(details);
      if (location) {
        mapInstanceRef.current.panTo(location);
        mapInstanceRef.current.setZoom(15);
      }
    }
  }, [userLocation]);

  const addRecommendationMarkers = React.useCallback((recommendations: LocationRecommendation[]) => {
    if (!mapInstanceRef.current || !window.google?.maps?.Marker) return;

    clearRecommendationMarkers();

    const bounds = new window.google.maps.LatLngBounds();
    let placedCount = 0;
    const mappableRecommendations = recommendations.filter((item) => item.name);

    mappableRecommendations.forEach((recommendation) => {
      fetchPlaceGeometryFromGoogle(mapInstanceRef.current, recommendation.place_id, {
        queryFallback: recommendation.name,
        locationBias: userLocation || undefined,
      }).then((geometry) => {
        if (!geometry || !mapInstanceRef.current) return;

        const marker = new window.google.maps.Marker({
          map: mapInstanceRef.current,
          position: { lat: geometry.lat, lng: geometry.lng },
          title: recommendation.name,
        });

        marker.addListener('click', () => {
          fetchPlaceDetails(geometry.resolvedPlaceId || recommendation.place_id, recommendation.name);
        });

        recommendationMarkersRef.current.push(marker);
        bounds.extend({ lat: geometry.lat, lng: geometry.lng });
        placedCount += 1;
        setSuggestedPlacesCount(placedCount);

        if (placedCount === mappableRecommendations.length) {
          mapInstanceRef.current.fitBounds(bounds, 48);
        }
      });
    });
  }, [clearRecommendationMarkers, fetchPlaceDetails, userLocation]);

  React.useEffect(() => {
    if (!hasApiKey || !mapRef.current || mapInstanceRef.current) return undefined;

    let interval: NodeJS.Timeout | null = null;

    const initializeMap = () => {
      if (!window.google?.maps?.Map || !mapRef.current || mapInstanceRef.current) return;

      const center = userLocation || { lat: 40.749933, lng: -73.98633 };
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      mapInstanceRef.current = mapInstance;
      setMapReady(true);
    };

    if (window.google?.maps?.Map) {
      initializeMap();
    } else {
      interval = setInterval(() => {
        if (window.google?.maps?.Map) {
          if (interval) clearInterval(interval);
          initializeMap();
        }
      }, 100);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [hasApiKey, userLocation]);

  React.useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setMapTypeId(mapType);
  }, [mapType]);

  React.useEffect(() => {
    if (!mapReady || !pendingPlaceSelectionRef.current) return;

    const pending = pendingPlaceSelectionRef.current;
    pendingPlaceSelectionRef.current = null;
    fetchPlaceDetails(pending.placeId, pending.queryFallback);
  }, [fetchPlaceDetails, mapReady]);

  React.useEffect(() => {
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(location);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(location);
        }
      },
      () => undefined,
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, []);

  React.useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  React.useEffect(() => {
    if (!mapReady) return;

    const latestWithRecommendations = [...chatMessages]
      .reverse()
      .find((message) => message.locations_recommendations?.length);

    if (latestWithRecommendations?.locations_recommendations) {
      addRecommendationMarkers(latestWithRecommendations.locations_recommendations);
    }
  }, [chatMessages, addRecommendationMarkers, mapReady]);

  React.useEffect(() => {
    loadConversationsList();
  }, [loadConversationsList]);

  // Track whether the current chat already has a trip, so a repeat CREATE_TRIP
  // proposal is blocked instead of creating a duplicate.
  React.useEffect(() => {
    if (!conversationId) {
      setConversationTripId(undefined);
      return undefined;
    }
    let cancelled = false;
    getTrips()
      .then((trips) => {
        if (cancelled) return;
        const match = trips.find((trip) => trip.conversation_id === conversationId);
        setConversationTripId(match ? extractTripUuid(match.id) : undefined);
      })
      .catch(() => {
        if (!cancelled) setConversationTripId(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  React.useEffect(() => {
    if (!isSessionMenuOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!sessionMenuRef.current?.contains(event.target as Node)) {
        setIsSessionMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isSessionMenuOpen]);

  React.useEffect(() => {
    return webSocket.subscribe((message: WebSocketMessage) => {
      const incomingConversationId = message.conversation_id || (message as any).conversationId;

      if (incomingConversationId) {
        setConversationId(incomingConversationId);
        loadConversationsList();
      }

      if (conversationId && incomingConversationId && incomingConversationId !== conversationId) {
        return;
      }

      const status = (message as any).status || (message as any).body?.status || (message as any).response?.status;
      const text =
        (message as any).text ||
        (message as any).body?.text ||
        (message as any).response?.text ||
        (message as any).message ||
        (message as any).response?.message;
      const error = (message as any).error || (message as any).body?.error || (message as any).response?.error;

      if (status === 'processing' || message.message === 'Connection accepted') {
        return;
      }

      if (error) {
        finishAssistantMessage(`Error: ${error}`, true);
        return;
      }

      if (status === 'complete' || text) {
        let responseText = text || '';
        let agentResponse: AgentResponse | null = null;

        if ((message as any).response && typeof (message as any).response === 'object' && (message as any).response.intent) {
          agentResponse = (message as any).response as AgentResponse;
          responseText = agentResponse.message;
        } else {
          const parsed = parseAgentResponse(responseText);
          agentResponse = parsed.response;
          responseText = agentResponse?.message || responseText;
        }

        finishAssistantMessage(responseText, false, agentResponse || undefined);
      }
    });
  }, [conversationId, finishAssistantMessage, loadConversationsList, webSocket]);

  React.useEffect(() => () => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
    clearRecommendationMarkers();
  }, [clearRecommendationMarkers]);

  const handleSendMessage = async (message = currentInput) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isWaitingForResponse) return;

    setCurrentInput('');
    setChatMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        message: trimmedMessage,
        isUser: true,
        timestamp: new Date(),
      },
      {
        id: `loading-${Date.now()}`,
        message: '',
        isUser: false,
        timestamp: new Date(),
        isLoading: true,
      },
    ]);
    setIsWaitingForResponse(true);

    try {
      await webSocket.sendMessage(trimmedMessage, conversationId || undefined, {
        user_time: new Date().toISOString(),
        user_location: userLocation
          ? { lat: userLocation.lat.toString(), lng: userLocation.lng.toString() }
          : undefined,
        // REQ-15: the place the user is viewing — ground truth for the agent,
        // so "is it open now?" needs no second Places lookup.
        selected_place: selectedPlace
          ? {
              place_id: selectedPlaceId || undefined,
              name: selectedPlace.name,
              address: selectedPlace.formatted_address,
              rating: selectedPlace.rating,
              user_ratings_total: selectedPlace.user_ratings_total,
              price_level: selectedPlace.price_level,
              phone: selectedPlace.formatted_phone_number,
              website: selectedPlace.website,
              open_now: selectedPlace.opening_hours?.open_now,
              weekday_hours: selectedPlace.opening_hours?.weekday_text,
              types: selectedPlace.types,
            }
          : undefined,
      });

      responseTimeoutRef.current = setTimeout(() => {
        finishAssistantMessage('The assistant is taking longer than expected. Please try again.', true);
      }, 60000);
    } catch (err: any) {
      finishAssistantMessage(err.message || 'Could not send message to the assistant.', true);
    }
  };

  const resolveActionRequest = React.useCallback((
    messageId: string,
    resolution: ActionResolution,
    note: string,
    tripId?: string,
  ) => {
    // Remember the trip created for this chat so any later CREATE_TRIP proposal
    // is blocked as a duplicate.
    if (resolution === 'confirmed' && tripId) {
      setConversationTripId(tripId);
    }
    setChatMessages((current) => {
      const updated = current.map((message) => (
        message.id === messageId
          ? {
              ...message,
              actionResolution: resolution,
              createdTripId: tripId,
              actionResolutionNote: note,
            }
          : message
      ));

      if (resolution !== 'dismissed' && !tripId) {
        updated.push({
          id: `action-${resolution}-${Date.now()}`,
          message: resolution === 'confirmed' ? `✓ ${note}` : `⚠ ${note}`,
          isUser: false,
          timestamp: new Date(),
          isError: resolution === 'failed',
        });
      }

      return updated;
    });
  }, []);

  const handleRecommendationSelect = (recommendation: LocationRecommendation) => {
    if (!recommendation.place_id && !recommendation.name) return;
    setSelectedRecommendation(recommendation);
    fetchPlaceDetails(recommendation.place_id || recommendation.name, recommendation.name);
  };

  const handleNewChat = () => {
    resetChatSession();
    setIsSessionMenuOpen(false);
  };

  const handleToggleSessionMenu = () => {
    setIsSessionMenuOpen((current) => {
      const next = !current;
      if (next) {
        loadConversationsList();
      }
      return next;
    });
  };

  const handleSelectConversation = async (summary: ConversationSummary) => {
    setIsSessionMenuOpen(false);

    if (summary.conversation_id === conversationId) {
      return;
    }

    setIsLoadingConversation(true);
    setConversationId(summary.conversation_id);
    setSelectedPlace(null);
    setSelectedPlaceId(null);
    setSelectedRecommendation(null);
    setSuggestedPlacesCount(0);
    clearRecommendationMarkers();

    try {
      const detail = await getConversation(summary.conversation_id);
      setChatMessages(formatConversationHistory(detail.messages));
    } catch (err: any) {
      setChatMessages([{
        id: `conversation-error-${Date.now()}`,
        message: `Could not load conversation history: ${err.message}`,
        isUser: false,
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsLoadingConversation(false);
    }
  };

  const statusLabel = webSocket.isConnected
    ? 'Ask anything, anywhere'
    : webSocket.isConnecting
    ? 'Connecting...'
    : 'Offline';

  return (
    <DashboardLayout activeSection="ask-ai" wide>
      <div className="v2-ask-ai-page">
        <section className="v2-ask-ai-chat" aria-label="Ask AI chat">
          <header className="v2-ask-ai-chat__header">
            <div className="v2-ask-ai-chat__session-menu" ref={sessionMenuRef}>
              <button
                type="button"
                className="v2-ask-ai-chat__session"
                onClick={handleToggleSessionMenu}
                aria-expanded={isSessionMenuOpen}
                aria-haspopup="listbox"
              >
                {sessionLabel}
                <span aria-hidden>▾</span>
              </button>

              {isSessionMenuOpen && (
                <div className="v2-ask-ai-chat__session-dropdown" role="listbox" aria-label="Chat sessions">
                  <button
                    type="button"
                    className={`v2-ask-ai-chat__session-option ${!conversationId ? 'v2-ask-ai-chat__session-option--active' : ''}`}
                    onClick={handleNewChat}
                  >
                    <strong>New chat</strong>
                    <span>Start a fresh conversation</span>
                  </button>

                  <div className="v2-ask-ai-chat__session-divider" role="separator" />

                  {isLoadingConversations && (
                    <p className="v2-ask-ai-chat__session-state">Loading previous chats...</p>
                  )}

                  {!isLoadingConversations && conversationsError && (
                    <p className="v2-ask-ai-chat__session-state v2-ask-ai-chat__session-state--error">
                      {conversationsError}
                    </p>
                  )}

                  {!isLoadingConversations && !conversationsError && conversations.length === 0 && (
                    <p className="v2-ask-ai-chat__session-state">No previous chats yet.</p>
                  )}

                  {!isLoadingConversations && conversations.map((summary) => (
                    <button
                      type="button"
                      key={summary.conversation_id}
                      className={`v2-ask-ai-chat__session-option ${
                        conversationId === summary.conversation_id ? 'v2-ask-ai-chat__session-option--active' : ''
                      }`}
                      onClick={() => handleSelectConversation(summary)}
                    >
                      <strong>{truncateConversationPreview(summary.preview)}</strong>
                      {summary.updated_at ? (
                        <span>{formatConversationTimestamp(summary.updated_at)}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="v2-ask-ai-chat__status">
              <span className={`v2-ask-ai-chat__status-dot ${webSocket.isConnected ? 'v2-ask-ai-chat__status-dot--online' : ''}`} />
              {statusLabel}
            </p>
          </header>

          <div className="v2-ask-ai-chat__messages">
            <AssistantMessage>{welcomeMessage}</AssistantMessage>

            {isLoadingConversation && (
              <AssistantMessage isLoading>Loading conversation history...</AssistantMessage>
            )}

            {chatMessages.map((message) => (
              message.isUser ? (
                <UserMessage key={message.id}>{message.message}</UserMessage>
              ) : (
                <React.Fragment key={message.id}>
                  <AssistantMessage isLoading={message.isLoading} isError={message.isError}>
                    {message.isLoading ? 'Thinking...' : message.message}
                  </AssistantMessage>
                  {!message.isLoading && message.action_request && (
                    <ChatAttachment>
                      <ActionRequestCard
                        request={message.action_request}
                        resolution={message.actionResolution}
                        createdTripId={message.createdTripId}
                        resolutionNote={message.actionResolutionNote}
                        backTo="/ask-ai"
                        conversationId={conversationId || undefined}
                        existingTripId={conversationTripId}
                        fallbackActivities={collectFallbackActivities(message)}
                        onResolve={(resolution, note, tripId) => resolveActionRequest(message.id, resolution, note, tripId)}
                      />
                    </ChatAttachment>
                  )}
                  {!message.isLoading && !message.action_request && message.trip_draft && message.intent === 'CONFIRM_TRIP' && (
                    <ChatAttachment>
                      <TripDraftCard
                        draft={message.trip_draft}
                        resolution={message.actionResolution}
                        createdTripId={message.createdTripId}
                        resolutionNote={message.actionResolutionNote}
                        backTo="/ask-ai"
                        conversationId={conversationId || undefined}
                        existingTripId={conversationTripId}
                        fallbackActivities={collectFallbackActivities(message)}
                        onResolve={(resolution, note, tripId) => resolveActionRequest(message.id, resolution, note, tripId)}
                      />
                    </ChatAttachment>
                  )}
                  {message.locations_recommendations && message.locations_recommendations.length > 0 && (
                    <div className="v2-ask-ai-chat__recommendations">
                      {message.locations_recommendations.map((recommendation) => (
                        <button
                          type="button"
                          key={`${message.id}-${recommendation.place_id || recommendation.name}`}
                          className={`v2-ask-ai-chat__recommendation ${
                            selectedRecommendation?.name === recommendation.name
                            && selectedRecommendation?.place_id === recommendation.place_id
                              ? 'v2-ask-ai-chat__recommendation--active'
                              : ''
                          }`}
                          onClick={() => handleRecommendationSelect(recommendation)}
                        >
                          {recommendation.name}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              )
            ))}
            <div ref={chatMessagesEndRef} />
          </div>

          <div className="v2-ask-ai-chat__prompts">
            {promptChips.map((chip) => (
              <button
                type="button"
                key={chip.label}
                onClick={() => handleSendMessage(chip.label)}
                disabled={isWaitingForResponse}
              >
                <span aria-hidden>{chip.icon}</span>
                {chip.label}
              </button>
            ))}
          </div>

          <div className="v2-ask-ai-chat__input">
            <button type="button" className="v2-ask-ai-chat__attach" aria-label="Attach file">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <input
              type="text"
              value={currentInput}
              onChange={(event) => setCurrentInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              placeholder="Ask about a destination, activity, or trip idea..."
              aria-label="Ask about a destination, activity, or trip idea"
              disabled={isWaitingForResponse}
            />
            <button
              type="button"
              className="v2-ask-ai-chat__send"
              onClick={() => handleSendMessage()}
              disabled={!currentInput.trim() || isWaitingForResponse || !webSocket.isConnected}
              aria-label="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="m22 2-7 20-4-9-9-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 2 11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </section>

        <aside className="v2-map-sidebar" aria-label="Map and place details">
          <div className="v2-map-card">
            {hasApiKey ? (
              <>
                <div ref={mapRef} className="v2-map-card__canvas" />
                <div className="v2-map-card__controls">
                  <div className="v2-map-card__type">
                    <button
                      type="button"
                      className={mapType === 'roadmap' ? 'v2-map-card__type--active' : undefined}
                      onClick={() => setMapType('roadmap')}
                    >
                      Map
                    </button>
                    <button
                      type="button"
                      className={mapType === 'satellite' ? 'v2-map-card__type--active' : undefined}
                      onClick={() => setMapType('satellite')}
                    >
                      Satellite
                    </button>
                  </div>
                  <div className="v2-map-card__zoom">
                    <button type="button" aria-label="Zoom in" onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) + 1)}>+</button>
                    <button type="button" aria-label="Zoom out" onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) - 1)}>−</button>
                  </div>
                </div>
                <div className="v2-map-card__badge">
                  <span aria-hidden>📍</span>
                  {suggestedPlacesCount} {suggestedPlacesCount === 1 ? 'place' : 'places'} suggested
                </div>
              </>
            ) : (
              <div className="v2-map-card__fallback">
                <p>Map preview unavailable</p>
                <span>Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the map.</span>
              </div>
            )}
          </div>

          <PlaceDetailsPanel place={selectedPlace} isLoading={isLoadingPlace} />
        </aside>
      </div>
    </DashboardLayout>
  );
};

const AssistantMessage: React.FC<{ children: React.ReactNode; isLoading?: boolean; isError?: boolean }> = ({
  children,
  isLoading = false,
  isError = false,
}) => {
  const messageText = typeof children === 'string' ? children : null;

  return (
    <div className={`v2-ask-ai-message v2-ask-ai-message--assistant ${isError ? 'v2-ask-ai-message--error' : ''}`}>
      <span className="v2-ask-ai-message__avatar" aria-hidden>+</span>
      {messageText && !isLoading ? (
        <FormattedChatMessage text={messageText} className="v2-ask-ai-message__bubble" />
      ) : (
        <p>{children}</p>
      )}
    </div>
  );
};

const UserMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="v2-ask-ai-message v2-ask-ai-message--user">
    <p>{children}</p>
  </div>
);

export default AskAiPage;
